import React, { useEffect } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import B2bRebateApi, {
  TenantRebateBackendData,
  TenantRebateTier
} from 'api/b2b/B2bRebateApi'
import { findByValue } from 'helpers/b2b'
import OrbitSession from 'hooks/useCookies'

import { rebatePeriodOptions } from '../../Registration/contants'

type Option = { label: string; value: string }

export default function RebateEdit() {
  const [searchParams] = useSearchParams()

  const companyId = searchParams.get('id') || ''

  const {
    control,
    watch,
    getValues,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      rebate: false,
      companyName: '',
      currency: '',
      rebatePeriod: rebatePeriodOptions[0],
      rebates: [{ tierName: '', from: '', to: '', percentage: '' }]
    }
  })

  const faberToken = OrbitSession.get('faberToken')
  const emailId = OrbitSession.get('email')

  // Mutation for updating rebate
  const { mutate: updateRebate, isLoading: isSubmitting } = useMutation(
    (data: TenantRebateBackendData) =>
      B2bRebateApi.updateRebate(data, faberToken),
    {
      onSuccess: () => {
        toast.success('Rebate updated successfully', {
          autoClose: 2000,
          hideProgressBar: false
        })
        // onSaveSuccess('#documents')
      },
      onError: (error) => {
        console.error('API Error:', error)
        toast.error('Failed to create rebate')
      }
    }
  )
  // mutation for fetching the existing rebate data
  const { data: rebateData, isLoading: rebateLoading } = useQuery(
    ['specific-rebate-details', companyId], // query key
    async () => {
      const { data } = await B2bRebateApi.getRebateById(companyId, faberToken)
      return data.data
    },
    {
      enabled: !!companyId,
      staleTime: 1000 * 60 * 30, // 30 minutes
      cacheTime: 1000 * 60 * 60, // 1 hour
      onError: (error) => {
        console.error('Failed to fetch countries:', error)
      }
    }
  )

  // Setting the value of forms for edit case
  useEffect(() => {
    if (rebateData) {
      setValue('companyName', rebateData.company_name)
      setValue('currency', rebateData.currency)
      if (rebateData?.tenant_rebate_tier?.length) {
        setValue('rebate', true)
        setValue(
          'rebatePeriod',
          findByValue(
            rebatePeriodOptions,
            rebateData.tenant_rebate_tier[0].rebate_period
          ) || rebatePeriodOptions[0]
        )
        setValue(
          'rebates',
          rebateData.tenant_rebate_tier.map((r) => {
            return {
              tierName: r.rebate_tier_name,
              from: String(r.from_amount ?? ''),
              to: String(r.to_amount ?? ''),
              percentage: String(r.rebate_percentage ?? '')
            }
          })
        )
      }
    }
  }, [rebateData, setValue])

  const { fields, append, remove } = useFieldArray({ control, name: 'rebates' })

  const isRebate = watch('rebate')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = (data: any) => {
    if (!data.rebate) {
      toast.info('Rebate is disabled')
      // onSaveSuccess?.('#documents')
      return
    }
    if (!companyId) {
      toast.error('Please select a company first from TDP Details list', {
        autoClose: 2000,
        hideProgressBar: false
      })
      return
    }
    // Transform form data to match API interface
    const tenantRebateTiers: TenantRebateTier[] = data.rebates.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (rebate: any) => ({
        rebate_tier_name: rebate.tierName,
        from_amount: Number(rebate.from),
        to_amount: Number(rebate.to),
        rebate_percentage: Number(rebate.percentage),
        rebate_period: data.rebatePeriod?.value.toUpperCase()
      })
    )

    // Get the first rebate tier for the main object (assuming the API expects this structure)
    const firstTier = data.rebates[0]
    // TODO: Remove the unwanted fields from the request body
    const requestBody: TenantRebateBackendData = {
      tenant_id: companyId,
      rebate_tier_name: firstTier.tierName,
      rebate_period: data.rebatePeriod?.value.toUpperCase(),
      from_amount: 2,
      to_amount: 20,
      rebate_percentage: 20,
      is_active: true,
      created_by: emailId || '',
      updated_by: emailId || '',
      tenant_rebate_tier: tenantRebateTiers
    }

    updateRebate(requestBody)
  }

  return (
    <Box>
      {rebateLoading ? (
        <CircularProgress />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display='flex' alignItems='center'>
            <Controller
              name='rebate'
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  }
                  label='Enable Rebate'
                />
              )}
            />
          </Box>

          {isRebate && (
            <>
              <Box display='flex' alignItems='center'>
                <Typography sx={{ mr: '10px' }}>Company Name</Typography>
                <Controller
                  name='companyName'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      id='companyName'
                      variant='outlined'
                      size='small'
                      disabled={true}
                      sx={{
                        width: '20%'
                      }}
                      {...field}
                    />
                  )}
                />
              </Box>

              <Box display='flex' alignItems='center'>
                <Typography sx={{ mr: '10px' }}>Currency</Typography>
                <Controller
                  name='currency'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      id='currency'
                      variant='outlined'
                      size='small'
                      disabled={true}
                      sx={{
                        width: '10%'
                      }}
                      {...field}
                    />
                  )}
                />
              </Box>

              <Box display='flex' alignItems='center'>
                <Typography sx={{ mr: '10px' }}>Rebate Period*</Typography>
                <Controller
                  name='rebatePeriod'
                  control={control}
                  rules={{ required: { value: true, message: 'Required' } }}
                  render={({ field }) => (
                    <Autocomplete<Option, false, true, false> // disableClearable=true, single, no freeSolo
                      options={rebatePeriodOptions}
                      getOptionLabel={(o) => o.label}
                      isOptionEqualToValue={(opt, val) =>
                        opt.value === val?.value
                      }
                      disablePortal
                      disableClearable
                      value={field.value} // Option | null
                      onChange={(_, v) => field.onChange(v)} // pass the Option
                      onBlur={field.onBlur}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size='small'
                          sx={{ width: 150 }}
                          error={!!errors.rebatePeriod}
                          helperText={errors.rebatePeriod?.message}
                        />
                      )}
                    />
                  )}
                />
              </Box>

              <Box>
                <TableContainer sx={{ mt: '20px' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tier Name</TableCell>
                        <TableCell>From Amount</TableCell>
                        <TableCell>To Amount</TableCell>
                        <TableCell>Percentage</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields?.map((item, index) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Controller
                              name={`rebates.${index}.tierName`}
                              control={control}
                              rules={{ required: 'Tier name is required' }}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  size='small'
                                  error={!!errors.rebates?.[index]?.tierName}
                                  helperText={
                                    errors.rebates?.[index]?.tierName?.message
                                  }
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <Controller
                              name={`rebates.${index}.from`}
                              control={control}
                              rules={{
                                required: 'From amount is required',
                                min: {
                                  value: 0,
                                  message: 'Must be 0 or greater'
                                }
                              }}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  size='small'
                                  type='number'
                                  // disabled={
                                  //   index !== 0 || fields.length - 1 != index
                                  // }
                                  error={!!errors.rebates?.[index]?.from}
                                  helperText={
                                    errors.rebates?.[index]?.from?.message
                                  }
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <Controller
                              name={`rebates.${index}.to`}
                              control={control}
                              rules={{
                                required: 'To amount is required',
                                min: {
                                  value: 0,
                                  message: 'Must be 0 or greater'
                                }
                              }}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  size='small'
                                  type='number'
                                  // disabled={fields.length - 1 != index}
                                  error={!!errors.rebates?.[index]?.to}
                                  helperText={
                                    errors.rebates?.[index]?.to?.message
                                  }
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <Controller
                              name={`rebates.${index}.percentage`}
                              control={control}
                              rules={{
                                min: {
                                  value: 0,
                                  message: 'Must be 0 or greater'
                                },
                                max: {
                                  value: 100,
                                  message: 'Must be 100 or less'
                                },
                                required: 'Percentage is required'
                              }}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  size='small'
                                  type='number'
                                  error={!!errors.rebates?.[index]?.percentage}
                                  helperText={
                                    errors.rebates?.[index]?.percentage?.message
                                  }
                                />
                              )}
                            />
                          </TableCell>
                          {fields.length - 1 === index && (
                            <TableCell>
                              <Button
                                onClick={() =>
                                  append({
                                    tierName: '',
                                    from: `${
                                      Number(
                                        +getValues(`rebates.${index}.to`)
                                      ) + 1
                                    }`,
                                    to: '',
                                    percentage: ''
                                  })
                                }
                              >
                                +
                              </Button>
                              <Button onClick={() => remove(index)}>-</Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}

          <Box sx={{ mt: '20px' }}>
            <Button
              type='submit'
              variant='contained'
              disableElevation
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </form>
      )}
    </Box>
  )
}
